/** Browser request boundary. This module is bundled separately and runs first. */
export const TELEMETRY_PATH = /^(?:\/api\/j(?:\/|$)|\/api\/events(?:\/|$)|\/api\/(?:export-completed|clone-beacon|origin-token-audit)(?:\/|$)|\/_vercel(?:\/|$)|\/9955e4fbf0b6e46a(?:\/|$)|\/f069f62742d6c1ef(?:\/|$)|\/149e9513-01fa-4fb0-aad4-566afd725d1b(?:\/|$))/;

export function createNetworkPolicy(baseUrl) {
  const base = new URL(baseUrl);
  const resolve = input => new URL(
    input && typeof input === 'object' && 'url' in input ? input.url : String(input),
    base.href
  );
  return {
    allowsRequest(input) {
      try {
        const url = resolve(input);
        return url.protocol === 'blob:' || url.protocol === 'data:' ||
          (url.origin === base.origin && !TELEMETRY_PATH.test(url.pathname));
      } catch { return false; }
    },
    allowsConnection(input) {
      try {
        const url = resolve(input);
        const origin = url.origin.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
        return origin === base.origin && !TELEMETRY_PATH.test(url.pathname);
      } catch { return false; }
    }
  };
}

/** Install once, including when the document restores from the back-forward cache. */
export function installNetworkGuard(scope = window) {
  const key = Symbol.for('mockup-studio.network-guard');
  if (scope[key]) return scope[key];
  const policy = createNetworkPolicy(scope.location.href);
  const nativeFetch = scope.fetch.bind(scope);
  scope.fetch = function(input, init) {
    if (!policy.allowsRequest(input)) {
      return Promise.reject(new TypeError('Studio blocked a non-studio request'));
    }
    return nativeFetch(input, init);
  };
  const xhr = scope.XMLHttpRequest?.prototype;
  if (xhr) {
    const open = xhr.open;
    xhr.open = function(method, url, ...args) {
      if (!policy.allowsRequest(url)) {
        throw new scope.DOMException('Studio blocked a non-studio request', 'SecurityError');
      }
      return open.call(this, method, url, ...args);
    };
  }
  // No beacon receiver exists locally. Do not queue the captured telemetry.
  Object.defineProperty(scope.navigator, 'sendBeacon', {configurable: true, value: () => false});
  for (const name of ['WebSocket', 'EventSource']) {
    const Native = scope[name];
    if (!Native) continue;
    scope[name] = new Proxy(Native, {
      construct(Target, args, NewTarget) {
        if (!policy.allowsConnection(args[0])) {
          throw new scope.DOMException('Studio blocked a non-studio connection', 'SecurityError');
        }
        return Reflect.construct(Target, args, NewTarget);
      }
    });
  }
  Object.defineProperty(scope, key, {value: policy});
  return policy;
}
